/**
 * Carousel Component using Embla Carousel
 *
 * Initializes touch-enabled horizontal carousels with:
 * - Prev/next navigation buttons
 * - Dot indicators
 * - HTMX afterSwap re-initialization support
 */

import EmblaCarousel, {
    EmblaCarouselType,
    EmblaOptionsType
} from '../../vendor/embla-carousel.js';

const CAROUSEL_SELECTOR = '[data-carousel]';
const VIEWPORT_SELECTOR = '.ox-carousel__viewport';
const PREV_BTN_SELECTOR = '[data-carousel-prev]';
const NEXT_BTN_SELECTOR = '[data-carousel-next]';
const DOTS_CONTAINER_SELECTOR = '[data-carousel-dots]';

const DOT_CLASS = 'ox-carousel__dot';
const DOT_ACTIVE_CLASS = 'ox-carousel__dot--active';

// Store carousel instances for cleanup
const carouselInstances = new Map<HTMLElement, EmblaCarouselType>();

/**
 * Default Embla options
 */
const defaultOptions: EmblaOptionsType = {
    loop: false,
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false
};

/**
 * Creates dot indicators for the carousel
 */
function createDots(
    emblaApi: EmblaCarouselType,
    dotsContainer: HTMLElement
): void {
    const scrollSnaps = emblaApi.scrollSnapList();

    // Clear existing dots
    dotsContainer.innerHTML = '';

    // Create dot for each slide
    scrollSnaps.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = DOT_CLASS;
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

        dot.addEventListener('click', () => {
            emblaApi.scrollTo(index);
        });

        dotsContainer.appendChild(dot);
    });

    // Set initial active state
    updateDots(emblaApi, dotsContainer);
}

/**
 * Updates the active state of dot indicators
 */
function updateDots(
    emblaApi: EmblaCarouselType,
    dotsContainer: HTMLElement
): void {
    const selectedIndex = emblaApi.selectedScrollSnap();
    const dots = dotsContainer.querySelectorAll<HTMLButtonElement>(
        `.${DOT_CLASS}`
    );

    dots.forEach((dot, index) => {
        dot.classList.toggle(DOT_ACTIVE_CLASS, index === selectedIndex);
        dot.setAttribute(
            'aria-current',
            index === selectedIndex ? 'true' : 'false'
        );
    });
}

/**
 * Updates the disabled state of navigation buttons
 */
function updateNavButtons(
    emblaApi: EmblaCarouselType,
    prevBtn: HTMLButtonElement | null,
    nextBtn: HTMLButtonElement | null
): void {
    if (prevBtn) {
        prevBtn.disabled = !emblaApi.canScrollPrev();
        prevBtn.setAttribute(
            'aria-disabled',
            String(!emblaApi.canScrollPrev())
        );
    }
    if (nextBtn) {
        nextBtn.disabled = !emblaApi.canScrollNext();
        nextBtn.setAttribute(
            'aria-disabled',
            String(!emblaApi.canScrollNext())
        );
    }
}

/**
 * Initializes a single carousel element
 */
function initCarousel(carouselEl: HTMLElement): void {
    // Skip if already initialized
    if (carouselInstances.has(carouselEl)) {
        return;
    }

    const viewport = carouselEl.querySelector<HTMLElement>(VIEWPORT_SELECTOR);
    if (!viewport) {
        console.warn('Carousel viewport not found');
        return;
    }

    // Get options from data attributes
    const customOptions: EmblaOptionsType = {};

    if (carouselEl.dataset.carouselLoop === 'true') {
        customOptions.loop = true;
    }

    if (carouselEl.dataset.carouselAlign) {
        customOptions.align = carouselEl.dataset
            .carouselAlign as EmblaOptionsType['align'];
    }

    const options = { ...defaultOptions, ...customOptions };

    // Initialize Embla
    const emblaApi = EmblaCarousel(viewport, options);
    carouselInstances.set(carouselEl, emblaApi);

    // Get navigation elements
    const prevBtn =
        carouselEl.querySelector<HTMLButtonElement>(PREV_BTN_SELECTOR);
    const nextBtn =
        carouselEl.querySelector<HTMLButtonElement>(NEXT_BTN_SELECTOR);
    const dotsContainer = carouselEl.querySelector<HTMLElement>(
        DOTS_CONTAINER_SELECTOR
    );

    // Set up prev/next button handlers
    if (prevBtn) {
        prevBtn.addEventListener('click', () => emblaApi.scrollPrev());
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', () => emblaApi.scrollNext());
    }

    // When there is only a single snap point (everything fits on one page),
    // hide the dots and force-disable both nav buttons so the controls don't
    // appear interactive.
    const singlePage = emblaApi.scrollSnapList().length <= 1;

    // Create dots if container exists (skip for single-page carousels)
    if (dotsContainer) {
        if (singlePage) {
            dotsContainer.innerHTML = '';
        } else {
            createDots(emblaApi, dotsContainer);
        }
    }

    if (singlePage) {
        // Hide the entire nav bar (arrows + dots) when everything fits on one page
        const navContainer =
            carouselEl.querySelector<HTMLElement>('.ox-carousel__nav');
        if (navContainer) {
            navContainer.hidden = true;
        }
    } else {
        // Update UI on scroll
        emblaApi.on('select', () => {
            updateNavButtons(emblaApi, prevBtn, nextBtn);
            if (dotsContainer) {
                updateDots(emblaApi, dotsContainer);
            }
        });

        // Initial UI state
        updateNavButtons(emblaApi, prevBtn, nextBtn);
    }
}

/**
 * Destroys a carousel instance
 */
function destroyCarousel(carouselEl: HTMLElement): void {
    const emblaApi = carouselInstances.get(carouselEl);
    if (emblaApi) {
        emblaApi.destroy();
        carouselInstances.delete(carouselEl);
    }
}

/**
 * Initializes all carousels in the document
 */
function initCarousels(): void {
    document
        .querySelectorAll<HTMLElement>(CAROUSEL_SELECTOR)
        .forEach(carouselEl => {
            initCarousel(carouselEl);
        });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
} else {
    initCarousels();
}

// Re-initialize after HTMX swaps (for dynamic content)
document.body.addEventListener('htmx:afterSwap', () => {
    initCarousels();
});

// Export for potential use elsewhere
export { initCarousels, initCarousel, destroyCarousel };
